// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import update from 'immutability-helper';
import { FormattedMessage } from 'react-intl';
import { getFilePathExtension } from '../../../../../utils/string';
import ReportModalContainer from '../../../../report/container/ReportModalContainer';
import type {Motive, MotiveKey} from '../../../../report/duck';
import UploadBox, { uploadErrorMessages, validFormats } from '../../../components/UploadBox/UploadBox';
import Dropzone from 'react-dropzone';
import { FormValidator } from '../../../../../utils/form';
import ButtonGreyBox from '../../../../../elements/ButtonGreyBox/ButtonGreyBox';
import { scroller } from 'react-scroll';
import { Element } from 'react-scroll/modules';
import { Mobile } from '../../../../../utils/responsive';
import FloatingBottom from '../../../../../elements/FloatingBottom/FloatingBottom';
import RadioButton from '../../../../../elements/RadioButton/RadioButton';
import { dataURItoBlob } from '../../../../../utils/file';

import style from './RegisterPurchase.scss';
import commonStyle from '../../../components/OrderCommon.scss';
import icons from '../../../../../assets/styles/icons.scss';
import formStyle from '../../../../../assets/styles/form.scss';
import inputStyle from '../../../../../assets/styles/form.scss';
import buttonStyle from '../../../../../assets/styles/button.scss';

import type { Node } from 'react';
import type { LocaleState } from '../../../../locale/duck';
import type { Order, OrderItem } from '../../../../../entities';
import type { Item } from '../../../../../elements/SingleProduct/SingleProduct';
import type { HTTPError } from '../../../../../error';
import type { FormValidationResult } from '../../../../../utils/form';

type Props = {
  +locale: LocaleState;
  +buildProducts: (renderContent?: (item: Item) => Node) => Node;
  +buildSummary: () => Node;
  +uploadInvoiceFile: (orderId: string, itemNumber: string, imageData: FormData, ext: string) => void;
  +postIdentity: (orderId: string, identityData: FormData) => void;
  +postIdentityError: HTTPError;
  +progressPurchase: (orderId: string) => void;
  +isProgressing: boolean;
  +progressError: HTTPError;
  +itemLoadingMap: { [key: string]: boolean };
  +itemErrorMap: { [key: string]: HTTPError };
  +order: Order;
  +motives?: { [key: MotiveKey]: Motive[] };
};

type State = {
  invoiceURIMap: { [key: string]: string };
  fileTypeMap: { [key: string]: string };
  imageErrorMap: { [key: string]: string };
  idURIMap: { [key: string]: string };
  idErrorMap: { [key: string]: string[] };
  idType: {
    value: string;
    errors?: string[];
  };
  idNumber: {
    value: string;
    errors?: string[];
  };
  rucNumber: {
    value: string;
    errors?: string[];
  };
  reportModalVisible: boolean;
};

const documentOptions = {
  PE: [
    {
      id: 'id',
      intl: 'order.purchase.registerPurchase.document.type.option.id',
    },
    {
      id: 'frc',
      intl: 'order.purchase.registerPurchase.document.type.option.frc',
    },
  ],
  CO: [
    {
      id: 'id',
      intl: 'order.purchase.registerPurchase.document.type.option.id.CO',
    },
    {
      id: 'frc',
      intl: 'order.purchase.registerPurchase.document.type.option.frc',
    },
  ],
};

const SCROLL_OPTIONS = {
  duration: 350,
  delay: 0,
  smooth: 'easeInOutQuart',
  offset: -100,
};

export default class RegisterPurchase extends Component<Props, State> {
  formValidator: FormValidator;

  constructor(props: Props) {
    super(props);
    this.state = {
      invoiceURIMap: {},
      fileTypeMap: {},
      imageErrorMap: {},
      idURIMap: {},
      idErrorMap: {},
      idType: {
        value: '',
      },
      idNumber: {
        value: '',
      },
      rucNumber: {
        value: '',
      },
      reportModalVisible: false,
    };
    this.formValidator = new FormValidator({
      idType: {
        required: 'required',
      },
      idNumber: {
        required: 'required',
      },
    });
  }

  componentDidMount() {
    const { locale, order } = this.props;
    if (locale.domainCountry === 'PE' && order.shipments.length > 0 &&
      order.shipments[0].courier.requiresIdentity) {
      this.formValidator.replaceFieldValidators({
        rucNumber: {
          required: 'required',
        },
      });
    }
  }

  handleCloseReportModal = () => {
    this.setState({ reportModalVisible: false });
  };

  handleOpenReportModal = () => {
    this.setState({ reportModalVisible: true });
  };

  handleChangeFile = (acceptedFiles: File[], orderItem: OrderItem) => {
    if (acceptedFiles.length) {
      const { uploadInvoiceFile, order } = this.props;
      const ext = getFilePathExtension(acceptedFiles[0].name);
      const reader = new FileReader();
      reader.onload = () => {
        const fileAsDataURL = reader.result;
        this.setState((state: State) => update(state, {
          invoiceURIMap: {
            [`${orderItem.number}`]: { $set: fileAsDataURL.toString() },
          },
          fileTypeMap: {
            [`${orderItem.number}`]: { $set: acceptedFiles[0].type },
          },
          imageErrorMap: {
            $unset: [`${orderItem.number}`],
          },
        }));
      };
      reader.onabort = () => console.log('file reading was aborted');
      reader.onerror = () => console.log('file reading has failed');
      reader.readAsDataURL(acceptedFiles[0]);
      const formData = new FormData();
      formData.append('file', acceptedFiles[0]);
      formData.append('resourceGroup', 'EXTERNAL_INVOICE');
      uploadInvoiceFile(order.id, orderItem.number, formData, ext);
    }
  };

  handleRejectedFile = (rejectedFiles: File[], orderItem: OrderItem) => {
    if (rejectedFiles.length && rejectedFiles[0].type !== '') {
      let errorKey = 'default';
      if (!validFormats.includes(rejectedFiles[0].type)) {
        errorKey = 'format';
      } else if (rejectedFiles[0].size > 3145728) {
        errorKey = 'size';
      }
      this.setState((state: State) => update(state, {
        imageErrorMap: {
          [`${orderItem.number}`]: { $set: errorKey },
        },
      }));
    }
  };

  handleChangeIdFile = (acceptedFiles: File[], type: 'front' | 'back') => {
    if (acceptedFiles.length) {
      const reader = new FileReader();
      reader.onload = () => {
        const fileAsDataURL = reader.result;
        this.setState((state: State) => update(state, {
          idURIMap: {
            [type]: { $set: fileAsDataURL.toString() },
          },
          idErrorMap: {
            $unset: [type],
          },
        }));
      };
      reader.onabort = () => console.log('file reading was aborted');
      reader.onerror = () => console.log('file reading has failed');
      reader.readAsDataURL(acceptedFiles[0]);
    }
  };

  handleRejectedIdFile = (rejectedFiles: File[], type: 'front' | 'back') => {
    if (rejectedFiles.length && rejectedFiles[0].type !== '') {
      let errorKey = 'default';
      if (!validFormats.includes(rejectedFiles[0].type)) {
        errorKey = 'format';
      } else if (rejectedFiles[0].size > 3145728) {
        errorKey = 'size';
      }
      this.setState((state: State) => update(state, {
        idErrorMap: {
          [type]: { $set: errorKey },
        },
      }));
    }
  };

  renderContent = (item: Item): Node => {
    const { invoiceURIMap, fileTypeMap, imageErrorMap } = this.state;
    const { itemLoadingMap, itemErrorMap } = this.props;
    return (
      <UploadBox isLoading={itemLoadingMap[`${item.number}`]}
                 hasError={!!itemErrorMap[`${item.number}`] || !!imageErrorMap[`${item.number}`]}
                 preview={invoiceURIMap[`${item.number}`] || (!!(item: OrderItem).externalInvoice ?
                   (item: OrderItem).externalInvoice.image : '')}
                 fileType={fileTypeMap[`${item.number}`]}
                 hasUploaded={!!(item: OrderItem).externalInvoice}
                 loadingInl="order.purchase.registerPurchase.uploadBox.loading"
                 uploadIntl="order.purchase.registerPurchase.uploadBox.upload"
                 hintIntl="order.purchase.registerPurchase.uploadBox.hint"
                 successIntl="order.purchase.registerPurchase.uploadBox.success"
                 changeImageIntl="order.purchase.registerPurchase.uploadBox.change"
                 errorIntl={uploadErrorMessages[imageErrorMap[`${item.number}`]] || uploadErrorMessages['default']}
                 retryIntl="order.purchase.registerPurchase.uploadBox.retry"
                 handleChangeFile={(file: File[]) => this.handleChangeFile(file, item)}
                 handleRejectedFile={(file: File[]) => this.handleRejectedFile(file, item)}/>);
  };

  handleChange = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  };

  handleFocus = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  };

  handleBlur = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  };

  handleChangeIdType = (e: SyntheticInputEvent<HTMLInputElement>) => {
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.setState({
        idType: {
          value,
        },
      });
    }
  };

  validateForm = (): boolean => {
    const { locale, order } = this.props;
    const { idURIMap, idErrorMap, idType, idNumber, rucNumber } = this.state;
    let resp = true;
    let fieldToScroll = '';
    if (locale.domainCountry === 'PE' && order.shipments !== 0 && order.shipments[0].courier.requiresIdentity) {
      if (!idURIMap['front'] || !idURIMap['back'] || !!idErrorMap['front'] || !!idErrorMap['back']) {
        resp = false;
        fieldToScroll = 'documents';
        ['front', 'back'].map((type: string) => {
          if (!idURIMap[type]) {
            this.setState((state: State) => update(state, {
              idErrorMap: {
                [type]: { $set: 'required' },
              },
            }));
          }
        });
      }

      const errors = this.formValidator.validateForm({
        idType: idType.value,
        idNumber: idNumber.value,
        rucNumber: rucNumber.value,
      });
      if (errors.length) {
        resp = false;
        errors.forEach((error: FormValidationResult) => {
          this.setState((state: State) => update(state, {
            [error.field]: {
              errors: { $set: error.errors },
            },
          }));
        });
        fieldToScroll = errors[0].field;
      }
    }
    let up = {
      imageErrorMap: {},
    };
    for (let item of order.items) {
      if (!item.externalInvoice) {
        resp = false;
        fieldToScroll = 'invoices';
        up.imageErrorMap[`${item.number}`] = { $set: 'required' };
      }
    }

    if (fieldToScroll) {
      if (fieldToScroll === 'invoices') {
        this.setState((state: State) => update(state, up));
      }
      scroller.scrollTo(fieldToScroll, SCROLL_OPTIONS);
    }
    return resp;
  };

  progressOrder = () => {
    const isValid = this.validateForm();
    if (isValid) {
      const { locale, postIdentity, order, progressPurchase } = this.props;
      const { idNumber, rucNumber, idURIMap } = this.state;
      if (locale.domainCountry === 'PE' && order.shipments !== 0 && order.shipments[0].courier.requiresIdentity) {
        const identityData = new FormData();
        identityData.append('idNumber1', idNumber.value);
        if (!!rucNumber.value) {
          identityData.append('idNumber2', rucNumber.value);
        }
        identityData.append('frontImage', dataURItoBlob(idURIMap['front']));
        identityData.append('backImage', dataURItoBlob(idURIMap['back']));
        postIdentity(order.id, identityData);
      } else {
        progressPurchase(order.id);
      }
    } else {
      console.log('Not valid');
    }
  };

  render() {
    const { locale, buildProducts, buildSummary, order, motives } = this.props;
    const { idURIMap, idErrorMap, idType, idNumber, rucNumber, reportModalVisible } = this.state;

    const options = motives ? motives.purchaser.map((motive: Motive) => ({
      value: motive.name,
      label: motive.translations[locale.locale.toUpperCase()],
    })) : [];

    const idTypeOptionsContent = documentOptions[locale.domainCountry || 'PE']
      .map((document: { [key: string]: string }) => (
        <RadioButton key={document.id}
                     selected={idType.value}
                     value={document.id}
                     name={document.id}
                     handleChange={this.handleChangeIdType}>
          <FormattedMessage id={document.intl}>{
            txt => <span>{txt}</span>
          }</FormattedMessage>
        </RadioButton>
      ));
    const isVerifiedWithCourier = false;
    const idTypeError = idType.errors && FormValidator.buildErrorLabel(idType.errors, {
      required: 'order.purchase.registerPurchase.document.type.error.required',
    });
    const idNumberError = idNumber.errors && FormValidator.buildErrorLabel(idNumber.errors, {
      required: 'order.purchase.registerPurchase.document.number.error.required',
    });
    const rucNumberError = rucNumber.errors && FormValidator.buildErrorLabel(rucNumber.errors, {
      required: 'order.purchase.registerPurchase.document.ruc.error.required',
    });
    const idURIMapError = (!!idErrorMap['front'] || !!idErrorMap['back']) &&
      FormValidator.buildErrorLabel(['required'], {
        required: 'order.purchase.registerPurchase.uploadId.error.required',
      });
    const buttonContent = <Fragment>
      <FormattedMessage id="order.purchase.registerPurchase.register.button.title.one">{
        txt => <p className={style.buttonText}>{txt}</p>
      }</FormattedMessage>
      <FormattedMessage id="order.purchase.registerPurchase.register.button.title.two">{
        txt => <p className={style.buttonText}>{txt}</p>
      }</FormattedMessage>
    </Fragment>;
    return (
      <Fragment>
        <main className={commonStyle.mainWrap}>
        <section className={classNames(commonStyle.wrap, commonStyle.leftWrap)}>
          <FormattedMessage id="order.purchase.registerPurchase.products.title">{
            txt => <h2 className={commonStyle.title}>{(order.shipments.length > 0 &&
              order.shipments[0].courier.requiresIdentity) ? `1. ` : ''}{txt}</h2>
          }</FormattedMessage>
          <Element name="invoices">
            {buildProducts(this.renderContent)}
          </Element>
          {order.shipments.length > 0 && order.shipments[0].courier.requiresIdentity &&
          <div className={style.titleWrap}>
            <FormattedMessage id="order.purchase.registerPurchase.identity.title">{
              txt => <h2 className={commonStyle.title}>2. {txt}</h2>
            }</FormattedMessage>
            {isVerifiedWithCourier && <button className={classNames(buttonStyle.btn, style.edit)}>
              <i className={icons.iconEditMedium}/>
              <FormattedMessage id="order.purchase.registerPurchase.document.edit">{
                txt => <p>{txt}</p>
              }</FormattedMessage>
            </button>}
          </div>}
          {isVerifiedWithCourier &&
          <div className={style.verified}>
            <i className={icons.iconCheckCircleMedium}/>
            <FormattedMessage id="order.purchase.registerPurchase.document.verified">{
              txt => <p>{txt}</p>
            }</FormattedMessage>
          </div>}
          {!isVerifiedWithCourier && order.shipments.length > 0 &&
          order.shipments[0].courier.requiresIdentity &&
          <Fragment>
            <div className={formStyle.formWrap}>
              <div className={formStyle.row}>
                <FormattedMessage id="order.purchase.registerPurchase.document.type.label">{
                  txt => <label className={classNames(formStyle.label, formStyle.extraMargin)}>{txt}</label>
                }</FormattedMessage>
                <Element name="idType">
                  {idTypeOptionsContent}
                </Element>
                {idTypeError}
              </div>
              <div className={formStyle.row}>
                <FormattedMessage id="order.purchase.registerPurchase.document.number.label">{
                  txt => <label className={formStyle.label}>{txt}</label>
                }</FormattedMessage>
                <input type="tel"
                       className={classNames(inputStyle.input,
                         { [inputStyle.red]: !!idNumber.errors && !!idNumber.errors.length },
                         { [inputStyle.lightBlue]: !!idNumber.value })}
                       value={idNumber.value}
                       onChange={this.handleChange}
                       onFocus={this.handleFocus}
                       onBlur={this.handleBlur}
                       name="idNumber"/>
                {idNumberError}
              </div>
              {locale.domainCountry === 'PE' &&
              <div className={formStyle.row}>
                <FormattedMessage id="order.purchase.registerPurchase.document.ruc.label">{
                  txt => <label className={formStyle.label}>{txt}</label>
                }</FormattedMessage>
                <input type="tel"
                       className={classNames(inputStyle.input,
                         { [inputStyle.red]: !!rucNumber.errors && !!rucNumber.errors.length },
                         { [inputStyle.lightBlue]: !!rucNumber.value })}
                       value={rucNumber.value}
                       onChange={this.handleChange}
                       onFocus={this.handleFocus}
                       onBlur={this.handleBlur}
                       name="rucNumber"/>
                {rucNumberError}
              </div>}
            </div>
            <Element name="documents">
              <div className={style.idsWrap}>
                <div className={classNames(style.uploadSizerWrap, style.left)}>
                  <div className={style.uploadSquareWrap}>
                    <div className={style.uploadContentWrap}>
                      <Dropzone accept="image/jpeg, image/jpg, image/png"
                                multiple={false}
                                maxSize={3145728}
                                className={classNames(style.dropzone, {
                                  [style.error]: !!idErrorMap['front'],
                                })}
                                activeClassName={style.active}
                                onDropAccepted={(file: File[]) => this.handleChangeIdFile(file, 'front')}
                                onDropRejected={(file: File[]) => this.handleRejectedIdFile(file, 'front')}>
                        {!idURIMap['front'] &&
                        <Fragment>
                          <div className={style.button}>
                            <i className={icons.symbolPlus}/>
                          </div>
                          <FormattedMessage id="order.purchase.registerPurchase.uploadId.front">{
                            txt => <p>{txt}</p>
                          }</FormattedMessage>
                        </Fragment>}
                        {!!idURIMap['front'] &&
                        <img className={style.idImage}
                             src={idURIMap['front']} alt="idImage"/>}
                      </Dropzone>
                    </div>
                  </div>
                </div>
                <div className={classNames(style.uploadSizerWrap, style.right)}>
                  <div className={style.uploadSquareWrap}>
                    <div className={style.uploadContentWrap}>
                      <Dropzone accept="image/jpeg, image/jpg, image/png"
                                multiple={false}
                                maxSize={3145728}
                                className={classNames(style.dropzone, {
                                  [style.error]: !!idErrorMap['back'],
                                })}
                                activeClassName={style.active}
                                onDropAccepted={(file: File[]) => this.handleChangeIdFile(file, 'back')}
                                onDropRejected={(file: File[]) => this.handleRejectedIdFile(file, 'back')}>
                        {!idURIMap['back'] &&
                        <Fragment>
                          <div className={style.button}>
                            <i className={icons.symbolPlus}/>
                          </div>
                          <FormattedMessage id="order.purchase.registerPurchase.uploadId.back">{
                            txt => <p>{txt}</p>
                          }</FormattedMessage>
                        </Fragment>}
                        {!!idURIMap['back'] &&
                        <img className={style.idImage}
                             src={idURIMap['back']} alt="idImage"/>}
                      </Dropzone>
                    </div>
                  </div>
                </div>
                {idURIMapError}
              </div>
            </Element>
          </Fragment>}
        </section>
        <section className={classNames(commonStyle.wrap, commonStyle.rightWrap)}>
          {buildSummary()}
          <div className={style.onlyDefault}>
            <ButtonGreyBox buttonIntl="order.purchase.registerPurchase.register.button.button"
                           handleClick={this.progressOrder}>{buttonContent}</ButtonGreyBox>
          </div>
          <Mobile>
            <FloatingBottom offSetY={146}>
              <div className={style.mobileWrap}>
                {buttonContent}
                <FormattedMessage id="order.purchase.registerPurchase.register.button.button">{
                  txt => <button onClick={this.progressOrder}
                                 className={classNames(buttonStyle.btn, buttonStyle.lightBlue, style.pay)}>{txt}</button>
                }</FormattedMessage>
              </div>
            </FloatingBottom>
          </Mobile>
          <FormattedMessage id="order.purchase.inTransit.report.button">{
            txt => <button className={classNames(buttonStyle.btn, buttonStyle.redInverse, style.report)}
                           onClick={this.handleOpenReportModal}>{txt}</button>
          }</FormattedMessage>
        </section>
      </main>
        <ReportModalContainer color="lightBlue"
                              reportType="PURCHASER_TRACKING"
                              isOpen={reportModalVisible}
                              handleClose={this.handleCloseReportModal}
                              isDiy={order.isDiy}
                              id={order.id}
                              options={options}
                              successIntl="modal.report.purchaser.success.text"/>
      </Fragment>
    );
  }
};

RegisterPurchase.propTypes = {
  locale: PropTypes.object.isRequired,
  buildProducts: PropTypes.func.isRequired,
  buildSummary: PropTypes.func.isRequired,
  uploadInvoiceFile: PropTypes.func.isRequired,
  postIdentity: PropTypes.func.isRequired,
  postIdentityError: PropTypes.object,
  progressPurchase: PropTypes.func.isRequired,
  isProgressing: PropTypes.bool,
  progressError: PropTypes.object,
  itemLoadingMap: PropTypes.object.isRequired,
  itemErrorMap: PropTypes.object.isRequired,
  order: PropTypes.object,
  motives: PropTypes.object,
};



// WEBPACK FOOTER //
// ./src/modules/order/Purchase/register-purchase/components/RegisterPurchase.jsx